import {gsap} from 'gsap';

const DESKTOP_ACTIVE_W = 384;
const DESKTOP_INACTIVE_W = 256;
const DESKTOP_GAP = 40;
const MOBILE_GAP = 16;
const ARROW_SVG = `<svg viewBox="0 0 40 40"><path d="m39.25 21.75c1-1 1-2.5 0-3.5l-12.5-12.5c-1-1-2.5-1-3.5 0-1 1-1 2.5 0 3.5l8.25 8.25h-29c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5h29l-8.25 8.25c-1 1-1 2.5 0 3.5s2.5 1 3.5 0l12.5-12.5z"/></svg>`;
const MIN_CLONES_PER_SIDE = 10;
const SLIDE_SCAN_RADIUS = 6;

type CarouselSizes = {
    activeWidth: number;
    inactiveWidth: number;
    gap: number;
    inactiveMarginTop: number;
    inactiveMarginBottom: number;
};

type SlideMeasurement = {
    index: number;
    slide: HTMLElement;
    product: HTMLElement | null;
    distance: number;
};

class ProductCarousel {
    private slides: HTMLElement[];
    private readonly list: HTMLElement;
    private readonly track: HTMLElement;
    private index: number;
    private animating = false;
    private readonly n: number;
    private readonly focus: number;
    private readonly products = new WeakMap<HTMLElement, HTMLElement | null>();
    private x = 0;

    // ── Layout cache — invalidated on resize ──────────────────────────────
    private cachedSizes: CarouselSizes | null = null;
    private cachedTrackWidth = 0;
    private resizeTimer: ReturnType<typeof setTimeout> | null = null;

    // ── Drag — batch pointermove events to one rAF ────────────────────────
    private pendingDelta = 0;
    private dragFrame: number | null = null;

    constructor(el: HTMLElement) {
        this.track = el.querySelector('.splide__track')!;
        this.list = el.querySelector('.splide__list')!;
        const origSlides = Array.from(el.querySelectorAll<HTMLElement>('.splide__slide'));
        const cloneSets = Math.max(1, Math.ceil(MIN_CLONES_PER_SIDE / Math.max(origSlides.length, 1)));
        this.n = origSlides.length;
        this.focus = this.n * cloneSets;
        this.index = this.focus;

        const before = document.createDocumentFragment();
        for (let i = 0; i < cloneSets; i++) origSlides.forEach(s => before.appendChild(this.makeClone(s)));
        this.list.insertBefore(before, this.list.firstChild);
        for (let i = 0; i < cloneSets; i++) origSlides.forEach(s => this.list.appendChild(this.makeClone(s)));

        this.slides = Array.from(this.list.querySelectorAll<HTMLElement>('.splide__slide'));
        this.slides.forEach(s => this.cacheProduct(s));

        el.insertAdjacentHTML('beforeend', `<div class="splide__arrows">
            <button class="splide__arrow splide__arrow--prev">${ARROW_SVG}</button>
            <button class="splide__arrow splide__arrow--next">${ARROW_SVG}</button>
        </div>`);

        el.querySelector('.splide__arrow--prev')!.addEventListener('click', () => this.prev());
        el.querySelector('.splide__arrow--next')!.addEventListener('click', () => this.next());

        el.setAttribute('tabindex', '0');
        el.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') this.next();
            if (e.key === 'ArrowLeft') this.prev();
        });

        window.addEventListener('resize', this.onResize);
        this.readTrackWidth();
        this.render(false);
        this.drag();
    }

    // ── Resize ────────────────────────────────────────────────────────────

    private readonly onResize = (): void => {
        if (this.resizeTimer !== null) clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            this.resizeTimer = null;
            this.cachedSizes = null;
            this.readTrackWidth();
            this.render(false);
        }, 100);
    };

    private readTrackWidth(): void {
        this.cachedTrackWidth = this.track.offsetWidth;
        this.cachedSizes = null;
    }

    // ── Sizes ─────────────────────────────────────────────────────────────

    private getSizes(): CarouselSizes {
        if (this.cachedSizes) return this.cachedSizes;

        const tw = this.cachedTrackWidth || this.track.offsetWidth;

        if (tw >= 640) {
            return (this.cachedSizes = {
                activeWidth: DESKTOP_ACTIVE_W,
                inactiveWidth: DESKTOP_INACTIVE_W,
                gap: DESKTOP_GAP,
                inactiveMarginTop: 80,
                inactiveMarginBottom: 112,
            });
        }

        const multiplier = 0.72;
        const activeWidth = Math.min(DESKTOP_ACTIVE_W, Math.round(tw * multiplier));

        return (this.cachedSizes = {
            activeWidth,
            inactiveWidth: Math.min(DESKTOP_INACTIVE_W, Math.round(activeWidth * multiplier)),
            gap: MOBILE_GAP,
            inactiveMarginTop: 40,
            inactiveMarginBottom: 56,
        });
    }

    private slideLeft(index: number, s: CarouselSizes): number {
        const step = s.inactiveWidth + s.gap;
        if (index <= this.index) return index * step;
        return this.index * step + s.activeWidth + s.gap + (index - this.index - 1) * step;
    }

    private getOffset(s: CarouselSizes): number {
        return this.cachedTrackWidth / 2 - this.slideLeft(this.index, s) - s.activeWidth / 2;
    }

    // ── Render ────────────────────────────────────────────────────────────

    private render(animate = true): void {
        const s = this.getSizes();
        const offset = this.getOffset(s);
        this.list.style.gap = `${s.gap}px`;

        if (!animate) {
            gsap.set(this.list, {x: offset});
            this.x = offset;
            this.updateVisibleSlides();
            this.correctCenter();
            return;
        }

        gsap.to(this.list, {
            x: offset,
            duration: 0.3,
            ease: 'power2.out',
            onUpdate: () => {
                this.x = gsap.getProperty(this.list, 'x') as number;
                this.updateVisibleSlides();
            },
            onComplete: () => {
                this.animating = false;
                this.x = gsap.getProperty(this.list, 'x') as number;
                this.updateVisibleSlides();
                this.correctCenter();
            },
        });
    }

    private correctCenter(): void {
        const s = this.getSizes();
        for (let i = 0; i < 2; i++) {
            const center = this.x + this.slideLeft(this.index, s) + s.activeWidth / 2;
            const correction = this.cachedTrackWidth / 2 - center;
            if (Math.abs(correction) <= 0.5) return;
            this.x += correction;
            gsap.set(this.list, {x: this.x});
            this.updateVisibleSlides();
        }
    }

    // ── Slide management ──────────────────────────────────────────────────

    private prev(): void {
        if (this.animating) return;
        this.animating = true;
        this.index -= 1;

        const last = this.slides[this.slides.length - 1];
        const clone = this.makeClone(last);
        this.list.insertBefore(clone, this.list.firstChild);
        this.list.removeChild(last);
        this.slides.unshift(clone);
        this.slides.pop();
        this.index += 1;

        const s = this.getSizes();
        this.x -= s.inactiveWidth + s.gap;
        gsap.set(this.list, {x: this.x});
        this.render();
    }

    private next(): void {
        if (this.animating) return;
        this.animating = true;
        this.index += 1;

        const first = this.slides[0];
        const clone = this.makeClone(first);
        this.list.appendChild(clone);
        this.list.removeChild(first);
        this.slides.push(clone);
        this.slides.shift();
        this.index -= 1;

        const s = this.getSizes();
        this.x += s.inactiveWidth + s.gap;
        gsap.set(this.list, {x: this.x});
        this.render();
    }

    private shiftNext(): void {
        const clone = this.makeClone(this.slides[0]);
        this.list.appendChild(clone);
        this.list.removeChild(this.slides[0]);
        this.slides.push(clone);
        this.slides.shift();
        const s = this.getSizes();
        this.x += s.inactiveWidth + s.gap;
        gsap.set(this.list, {x: this.x});
    }

    private shiftPrev(): void {
        const last = this.slides[this.slides.length - 1];
        const clone = this.makeClone(last);
        this.list.insertBefore(clone, this.list.firstChild);
        this.list.removeChild(last);
        this.slides.unshift(clone);
        this.slides.pop();
        const s = this.getSizes();
        this.x -= s.inactiveWidth + s.gap;
        gsap.set(this.list, {x: this.x});
    }

    private makeClone(slide: HTMLElement): HTMLElement {
        const clone = slide.cloneNode(true) as HTMLElement;
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.add('splide__slide__clone');
        this.cacheProduct(clone);
        this.resetProductSize(clone);
        return clone;
    }

    private cacheProduct(slide: HTMLElement): HTMLElement | null {
        const product = slide.querySelector<HTMLElement>('.single-product-component');
        this.products.set(slide, product);
        return product;
    }

    private getProduct(slide: HTMLElement): HTMLElement | null {
        if (!this.products.has(slide)) return this.cacheProduct(slide);
        return this.products.get(slide) ?? null;
    }

    private resetProductSize(slide: HTMLElement): void {
        const product = this.getProduct(slide);
        if (!product) return;
        const {inactiveWidth, inactiveMarginTop, inactiveMarginBottom} = this.getSizes();
        product.style.width = `${inactiveWidth}px`;
        product.style.marginTop = `${inactiveMarginTop}px`;
        product.style.marginBottom = `${inactiveMarginBottom}px`;
    }

    // ── Drag ──────────────────────────────────────────────────────────────

    private drag(): void {
        let activePointerId: number | null = null;
        let startDragX = 0;
        let startDragY = 0;
        let lastDragX = 0;
        let hasDragged = false;
        let suppressClick = false;

        const onPointerDown = (e: PointerEvent) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            gsap.killTweensOf(this.list);
            this.animating = false;
            activePointerId = e.pointerId;
            startDragX = lastDragX = e.clientX;
            startDragY = e.clientY;
            hasDragged = false;
            document.addEventListener('pointermove', onPointerMove);
            document.addEventListener('pointerup', onPointerUp);
            document.addEventListener('pointercancel', onPointerUp);
        };

        const onPointerMove = (e: PointerEvent) => {
            if (activePointerId !== e.pointerId) return;
            const diff = e.clientX - lastDragX;
            if (diff === 0) return;

            if (!hasDragged) {
                const ax = Math.abs(e.clientX - startDragX);
                const ay = Math.abs(e.clientY - startDragY);
                lastDragX = e.clientX;
                if (ax <= 8 || ax <= ay) return;
                hasDragged = true;
                suppressClick = true;
                this.list.style.cursor = 'grabbing';
                this.list.setPointerCapture(e.pointerId);
            }

            lastDragX = e.clientX;
            this.translate(diff);
            e.preventDefault();
        };

        const onPointerUp = (e: PointerEvent) => {
            if (activePointerId !== e.pointerId) return;
            if (this.list.hasPointerCapture(e.pointerId)) this.list.releasePointerCapture(e.pointerId);
            activePointerId = null;
            this.list.style.cursor = 'grab';
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
            document.removeEventListener('pointercancel', onPointerUp);

            if (this.dragFrame !== null) {
                cancelAnimationFrame(this.dragFrame);
                this.dragFrame = null;
                this.x += this.pendingDelta;
                this.pendingDelta = 0;
                gsap.set(this.list, {x: this.x});
            }

            if (hasDragged) {
                this.snapToIndex(this.getClosestVisibleIndex());
                window.setTimeout(() => {
                    suppressClick = false;
                }, 350);
                return;
            }
            this.render();
        };

        this.list.style.touchAction = 'pan-y';
        this.list.style.cursor = 'grab';
        this.list.addEventListener('pointerdown', onPointerDown);
        this.list.addEventListener('dragstart', e => e.preventDefault());
        this.list.addEventListener('click', (e) => {
            if (!suppressClick) return;
            e.preventDefault();
            e.stopPropagation();
            suppressClick = false;
        }, true);
    }

    private translate(delta: number): void {
        this.pendingDelta += delta;
        if (this.dragFrame !== null) return;

        this.dragFrame = requestAnimationFrame(() => {
            this.dragFrame = null;
            this.x += this.pendingDelta;
            this.pendingDelta = 0;
            gsap.set(this.list, {x: this.x});
            this.updateVisibleSlides();
        });
    }

    // ── Snap / measure ────────────────────────────────────────────────────

    private snapToIndex(targetIndex: number): void {
        const steps = targetIndex - this.index;
        if (steps > 0) for (let i = 0; i < steps; i++) this.shiftNext();
        else if (steps < 0) for (let i = 0; i < -steps; i++) this.shiftPrev();
        this.animating = true;
        this.render();
    }

    private getClosestVisibleIndex(): number {
        return this.getClosestMeasurement(this.measureVisibleSlides())?.index ?? this.index;
    }

    private updateVisibleSlides(): void {
        const measurements = this.measureVisibleSlides();
        const closest = this.getClosestMeasurement(measurements);
        if (!closest) return;

        const s = this.getSizes();
        const range = s.inactiveWidth + s.gap;

        for (const {index, slide, product, distance} of measurements) {
            if (!product) continue;

            const progress = Math.max(0, 1 - distance / range);
            const ep = Math.pow(progress, 2.4);

            slide.classList.toggle('is-active', index === closest.index);

            product.style.width = `${s.inactiveWidth + (s.activeWidth - s.inactiveWidth) * ep}px`;
            product.style.marginTop = `${s.inactiveMarginTop * (1 - ep)}px`;
            product.style.marginBottom = `${s.inactiveMarginBottom * (1 - ep)}px`;
        }
    }

    private measureVisibleSlides(): SlideMeasurement[] {
        const s = this.getSizes();
        const halfTrack = this.cachedTrackWidth / 2;
        const cutoff = (s.inactiveWidth + s.gap) * (SLIDE_SCAN_RADIUS + 1);
        const start = Math.max(0, this.index - SLIDE_SCAN_RADIUS);
        const end = Math.min(this.slides.length - 1, this.index + SLIDE_SCAN_RADIUS);
        const out: SlideMeasurement[] = [];

        let cumX = this.slideLeft(start, s);
        for (let i = start; i <= end; i++) {
            const width = i === this.index ? s.activeWidth : s.inactiveWidth;
            const distance = Math.abs(this.x + cumX + width / 2 - halfTrack);
            if (distance < cutoff) {
                out.push({index: i, slide: this.slides[i], product: this.getProduct(this.slides[i]), distance});
            }
            cumX += width + s.gap;
        }
        return out;
    }

    private getClosestMeasurement(m: SlideMeasurement[]): SlideMeasurement | null {
        return m.reduce<SlideMeasurement | null>((best, cur) => (!best || cur.distance < best.distance ? cur : best), null);
    }
}

window.addEventListener('DOMContentLoaded', () =>
    document.querySelectorAll<HTMLElement>('.products-splide').forEach(el => {
        try {
            new ProductCarousel(el);
        } catch {
        }
    })
);